import type { LearnerClient, MomentView } from "@tutor/live";
import type { Milestone, SkillProgress } from "@tutor/protocol";
import { Celebration, Climb, MomentToast, type MomentKind } from "@tutor/ui";
import { useLocale, useT, type Translate } from "../i18n";
import { skillLabel } from "../insights/skill-labels";
import { stageReached, stageWords } from "../insights/stages";

// Progress inside a skill (ADR-0040). The step, the streak and every moment come from the
// engine on `item.graded`; the app only picks words and an icon for them.

/** The engine's track length (ADR-0040), for a moment that arrives before its progress. */
const DEFAULT_STEPS = 5;

/** The climb for the skill on the canvas (ADR-0043), once the engine has reported it. */
export function SkillSteps(props: {
  skillId: string;
  progress: SkillProgress | undefined;
  fresh?: boolean;
}) {
  const t = useT();
  const locale = useLocale();
  if (props.progress === undefined) return null;
  const { step, steps } = props.progress;
  const words = stageWords(t, skillLabel(props.skillId, locale), step, steps);
  // The drawing never stands alone: the stage is written beside it, and at the start the
  // next line says what moves the child on.
  return (
    <div className="skill-steps">
      <Climb step={step} steps={steps} label={words.label} fresh={props.fresh === true} />
      <p className="skill-steps-where" aria-hidden="true">
        {words.stage === null ? (
          <b>{words.count}</b>
        ) : (
          <>
            {step > 0 && `${words.count} · `}
            <b>{words.stage}</b>
          </>
        )}
      </p>
      {step === 0 && <p className="skill-steps-hint">{words.next}</p>}
    </div>
  );
}

/** The latest moment: a toast inside the skill, the celebration overlay for mastery. */
export function MomentLayer(props: {
  moment: MomentView | null;
  client: LearnerClient;
  /** The engine's progress per skill, for the number of steps on the moment's track. */
  progress: Readonly<Record<string, SkillProgress>>;
}) {
  const t = useT();
  const locale = useLocale();
  const { moment, client } = props;
  if (moment === null) return null;
  const skill = skillLabel(moment.skillId, locale);
  const dismiss = () => {
    client.dismissMoment();
  };
  if (moment.milestone.kind === "mastered") {
    return <Celebration title={skill} message={t("mastery.new", { skill })} onContinue={dismiss} />;
  }
  const steps = props.progress[moment.skillId]?.steps ?? DEFAULT_STEPS;
  const { kind, title, body } = momentCopy(moment.milestone, skill, steps, t);
  return (
    <MomentToast kind={kind} title={title} onDismiss={dismiss}>
      {body}
    </MomentToast>
  );
}

function momentCopy(
  milestone: Exclude<Milestone, { kind: "mastered" }>,
  skill: string,
  steps: number,
  t: Translate,
): { kind: MomentKind; title: string; body: string } {
  switch (milestone.kind) {
    case "stepReached":
      return {
        kind: "step",
        title: stageReached(t, milestone.step, steps),
        body: t("moment.step.body", { skill }),
      };
    case "harderItem":
      return {
        kind: "harder",
        title: t("moment.harder.title"),
        body: t("moment.harder.body", { skill }),
      };
    case "streak":
      return {
        kind: "streak",
        title: t("moment.streak.title", { count: milestone.length }),
        body: t("moment.streak.body"),
      };
    case "reviewKept":
      return {
        kind: "review",
        title: t("moment.review.title"),
        body: t("moment.review.body", { skill }),
      };
  }
}
